/**
 * Created by Carsten Bühler
 * User: CBuehler on 05.01.2016
 * Copyright: xdot GmbH 2015
 */
import ActionTypesCommon from '../constants/ActionTypesCommon';
import AsyncState from '../constants/AsyncState';

import { AppContext } from '../app-context';

import warning from '../utils/warning';

import { default as RemoteDataStore } from './RemoteDataStore';


import { getAsyncOperationStore } from './StoreFactory';

const AsyncOperationStore = getAsyncOperationStore(AppContext);


export default class FormStore extends RemoteDataStore {

    constructor(dispatcher) {
        super(dispatcher);
    }

    isEditing(formId) {
        const form = this.getEditFormState(formId);
        return !!(form && form.isEditing);
    }

    isSaving(formId) {
        const form = this.getEditFormState(formId);
        return !!(form && form.isSaving);
    }

    getEditFormState(formId) {
        return this.getState().editForms[ formId ];
    }

    isEdited(formId) {
        const form = this.getEditFormState(formId);
        return form && form.editItem !== form.originalItem;
    }

    hasChangedRemotely(formId) {
        const form = this.getEditFormState(formId),
            current = form.remoteItemName && this.getRemoteData(form.remoteItemName);
        return current && form.originalItem !== current.item;
    }

    getItem(formId) {
        const form = this.getEditFormState(formId);
        return form && form.editItem;
    }


    getOriginalItem(formId) {
        const form = this.getEditFormState(formId);
        return form && form.originalItem;
    }

    getValidation(formId) {
        const form = this.getEditFormState(formId);
        return form && form.validation;
    }

    getError(formId) {
        const form = this.getEditFormState(formId);
        return form && form.error;
    }

    getCurrentOperation(formId) {
        const operation = this.getEditFormState(formId).currentOperation;
        return operation && AppContext.getStore('AsyncOperationStore').getOperation(operation);
    }

    getInitialState() {
        return {
            ...super.getInitialState(),
            editForms: {}
        };
    }

    _isForm(formId) {
        return !!this.getEditFormState(formId);
    }

    _checkEditing(formId, editing) {
        const form = this.getEditFormState(formId);
        if (form.isEditing !== editing) {
            console.error('Form editing is not ' + editing, form);
        }
    }

    reduce(state, action) {


        switch (action.type) {

            case ActionTypesCommon.START_EDIT_IN_FORM:
                return this.constructor.startEditFormItem(state, action, this);

            case ActionTypesCommon.RESET_FORM:
                return this.constructor.resetFormItem(state, action);

            case ActionTypesCommon.END_EDIT_IN_FORM:
                if (this._isForm(action.meta.formId)) {
                    this._checkEditing(action.meta.formId, true);
                    return this.constructor.stopEditFormItem(state, action);
                }
                break;

            case ActionTypesCommon.EDIT_IN_FORM:
                if (this._isForm(action.meta.formId)) {
                    this._checkEditing(action.meta.formId, true);
                    return this.constructor.changedFormItem(state, action);
                } else {
                    console.error("Editing non existing Form");
                }
                break;

            case ActionTypesCommon.SUBMIT_FORM_ASYNC: {
                const { asyncState, key, formId } = action.meta;

                if (this._isForm(formId)) {
                    this._checkEditing(formId, true);
                    this.getDispatcher().waitFor([ AsyncOperationStore.getDispatchToken() ]);

                    switch (asyncState) {
                        case AsyncState.REQUESTED:
                            return this.constructor.mutateForm(state, action, {
                                currentOperation: key,
                                isSaving: true,
                                error: null
                            });
                        case AsyncState.SUCCESS:
                            if (action.meta.closeForm){
                                return this.constructor.stopEditFormItem(state, action);
                            } else {
                                return this.constructor.mutateForm(state, action, {
                                    isEditing: true,
                                    isSaving: false,
                                    error: null
                                });
                            }
                        case AsyncState.ERROR:
                            return this.constructor.mutateForm(state, action, {
                                isEditing: true,
                                isSaving: false,
                                error: action.payload
                            });
                        //no default
                    }

                }
                break;
            }

            default: {
                const meta = action.meta;

                if (meta && meta.formId && this._isForm(meta.formId)) {
                    this._checkEditing(meta.formId, true);
                    this.getDispatcher().waitFor([ AsyncOperationStore.getDispatchToken() ]);

                    switch (meta.asyncState) {
                        case AsyncState.SUCCESS:
                            return this.constructor.mutateForm(state, action, {
                                isEditing: !action.meta.closeForm
                            });
                        case AsyncState.ERROR:
                            return this.constructor.mutateForm(state, action, {
                                isEditing: true,
                                error: action.payload
                            });
                        //no default
                    }

                }
                break;
            }

        }

        return state;
    }


    static resetFormItem(state, action) {
        const editForms = state.editForms,
            formId = action.meta.formId,
            form = editForms[ formId ];

        return state = {
            ...state,
            editForms: {
                ...editForms,
                [ formId ]: {
                    ...form,
                    editItem: form.originalItem,
                    validation: {}
                }
            }

        };
    }


    static startEditFormItem(state, action, store) {
        const editForms = state.editForms,
            formId = action.meta.formId,
            form = editForms[ formId ],
            originalItem = action.payload;

        let remote = state.remote,
            remoteItemName;

        warning(!form || !form.isEditing, 'Form editing is editing');

        if (originalItem.api) {
            remoteItemName = formId + '_currentRemote';
            RemoteDataStore.initDataItem(remoteItemName, originalItem, undefined, store);
            //remote = RemoteReducer.initOrUpdateItem(state.remote, initAction.data);
            //var initAction= RemoteDataActions.CommonRemoteActionExtends.item('currentRemote', item);
        } else {
            console.warn('No api provided ==> Cannot listen to remote state', action);
        }

        return {
            ...state,
            remote,
            editForms: {
                ...editForms,
                [ formId ]: {
                    remoteItemName,
                    originalItem: originalItem.item || originalItem,
                    editItem: originalItem.item || originalItem,
                    validation: {},
                    isEditing: true,
                    isSaving: false
                }
            }

        };
    }

    static stopEditFormItem(state, action) {
        const editForms = {
            ...state.editForms
        };

        delete editForms[ action.meta.formId ];

        return {
            ...state,
            editForms
        };
    }

    static mutateForm(state, action, source) {
        const editForms = state.editForms,
            formId = action.meta.formId,
            form = editForms[ formId ];

        return {
            ...state,
            editForms: {
                ...editForms,
                [ formId ]: {
                    ...form,
                    ...source
                }
            }

        };
    }

    static changedFormItem(state, action) {
        const editForms = state.editForms,
            formId = action.meta.formId,
            form = editForms[ formId ],
            item = (action.payload && action.payload.item) || (action.data && action.data.item),
            validation = (action.payload && action.payload.validation) || (action.data && action.data.validation);


        return state = {
            ...state,
            editForms: {
                ...editForms,
                [ formId ]: {
                    ...form,
                    editItem: item,
                    validation: validation,
                    isEditing: true,
                    error: null
                }
            }

        };

    }

}




// WEBPACK FOOTER //
// ./js/stores/FormStore.js